import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  fetchTransporterActiveRuns,
  fetchTransporterRunHistory,
  type TransporterActiveRunDto,
  type TransporterRunHistoryDto,
} from "../lib/fetch-transporter-delivery-ops";
import { RunStatusBadge, runStatusDisplayLabel } from "../components/RunStatusBadge";
import { useTransporterWorkspace } from "../workspace/transporter-workspace-context";

function RunRow({ r }: { r: TransporterActiveRunDto }) {
  return (
    <tr>
      <td className="text-xs text-gray">
        <Link
          to="/workspace/run/$runId"
          params={{ runId: r.id }}
          className="font-mono text-[var(--primary,#1F7A6B)] hover:underline"
        >
          {r.id.slice(0, 8)}…
        </Link>
      </td>
      <td className="text-sm font-medium text-[#333333]">{r.dispensaryLegalName}</td>
      <td className="text-sm text-[#555555]">{r.driverFullName}</td>
      <td>
        <RunStatusBadge status={r.status} />
      </td>
      <td className="text-sm text-[#555555]">{r.metrcManifestNumber ?? "—"}</td>
      <td className="text-xs text-[#555555]">{r.manifestStatus}</td>
      <td className="text-xs text-td-muted-light">{r.updatedAt.slice(0, 19).replace("T", " ")}</td>
    </tr>
  );
}

function HistoryRow({ r }: { r: TransporterRunHistoryDto }) {
  const fmt = (iso: string | null) =>
    iso ? iso.slice(0, 19).replace("T", " ") : "—";
  return (
    <tr>
      <td className="text-xs text-gray">
        <Link
          to="/workspace/run/$runId"
          params={{ runId: r.id }}
          className="font-mono text-[var(--primary,#1F7A6B)] hover:underline"
        >
          {r.id.slice(0, 8)}…
        </Link>
      </td>
      <td className="text-sm font-medium text-[#333333]">{r.dispensaryLegalName}</td>
      <td className="text-sm text-[#555555]">{r.driverFullName}</td>
      <td>
        <RunStatusBadge status={r.status} />
      </td>
      <td className="text-sm text-[#555555]">{r.metrcManifestNumber ?? "—"}</td>
      <td className="text-xs text-[#555555]">{r.manifestStatus}</td>
      <td className="text-xs text-[#555555]">{fmt(r.completedAt)}</td>
      <td className="text-xs text-td-muted-light">{r.updatedAt.slice(0, 19).replace("T", " ")}</td>
    </tr>
  );
}

function addDaysIsoCalendar(yyyyMmDd: string, deltaDays: number): string {
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return dt.toISOString().slice(0, 10);
}

type RunsTab = "active" | "history";

export function RunsScreen() {
  const { transporterId, getToken } = useTransporterWorkspace();
  const [tab, setTab] = useState<RunsTab>("active");
  const [runs, setRuns] = useState<TransporterActiveRunDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const [historyRuns, setHistoryRuns] = useState<TransporterRunHistoryDto[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  // Migration 030 dropped `delivery_runs.voided_at` (vestigial — never written).
  // The "voided" terminal type is gone; only "completed" remains. The `"all"`
  // value is kept as a no-op selection for back-compat with bookmarked URLs.
  const [historyTerminal, setHistoryTerminal] = useState<"all" | "completed">("all");

  useEffect(() => {
    setStatusFilter("all");
    setSearch("");
    setHistoryTerminal("all");
    setTab("active");
  }, [transporterId]);

  useEffect(() => {
    if (!transporterId) {
      setRuns(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setError(null);
    const fetchOnce = async () => {
      try {
        const r = await fetchTransporterActiveRuns(getToken, transporterId);
        if (!cancelled) setRuns(r.runs);
      } catch (e) {
        if (!cancelled) {
          setRuns(null);
          setError(e instanceof Error ? e.message : "runs_load_failed");
        }
      }
    };
    void fetchOnce();
    // Mirror dispensary `DarkOrdersCard`'s 30s React Query `refetchInterval` so
    // terminal-stop transitions (e.g. driver fails delivery) stop showing as
    // active within 30s instead of requiring a full reload.
    const intervalId = setInterval(() => void fetchOnce(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [transporterId, getToken]);

  useEffect(() => {
    if (!transporterId || tab !== "history") {
      if (!transporterId) setHistoryRuns(null);
      return;
    }
    let cancelled = false;
    setHistoryError(null);
    setHistoryRuns(null);
    void (async () => {
      try {
        const r = await fetchTransporterRunHistory(getToken, transporterId, {
          limit: 75,
          status: historyTerminal === "all" ? undefined : historyTerminal,
        });
        if (!cancelled) setHistoryRuns(r.runs);
      } catch (e) {
        if (!cancelled) {
          setHistoryRuns(null);
          setHistoryError(e instanceof Error ? e.message : "runs_history_failed");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transporterId, getToken, tab, historyTerminal]);

  const statusOptions = useMemo(() => {
    if (!runs) return [];
    return [...new Set(runs.map((r) => r.status))].sort();
  }, [runs]);

  const statusCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of runs ?? []) {
      m.set(r.status, (m.get(r.status) ?? 0) + 1);
    }
    return m;
  }, [runs]);

  const filteredRuns = useMemo(() => {
    if (!runs) return [];
    let out = runs;
    if (statusFilter !== "all") {
      out = out.filter((r) => r.status === statusFilter);
    }
    const q = search.trim().toLowerCase();
    if (q.length > 0) {
      out = out.filter(
        (r) =>
          r.dispensaryLegalName.toLowerCase().includes(q) ||
          r.driverFullName.toLowerCase().includes(q) ||
          r.id.toLowerCase().includes(q),
      );
    }
    return out;
  }, [runs, statusFilter, search]);

  const staleDriverAssigned = useMemo(() => {
    if (!runs?.length) return { count: 0, threshold: "" as string };
    const threshold = addDaysIsoCalendar(new Date().toISOString().slice(0, 10), -2);
    const count = runs.filter(
      (r) => r.status === "driver_assigned" && r.manifestStatus === "pending" && r.updatedAt.slice(0, 10) < threshold,
    ).length;
    return { count, threshold };
  }, [runs]);

  if (!transporterId) {
    return (
      <section className="space-y-2">
        <p className="text-sm text-gray">
          Choose a transporter in the scope panel (platform admin) or use a linked dispatcher account.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <div>
        <p className="text-sm text-gray">
          Active in-flight runs and recent terminal history (completed), newest first.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-td-card-border pb-3">
        <button
          type="button"
          onClick={() => setTab("active")}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            tab === "active"
              ? "bg-green-light text-green ring-1 ring-green/20"
              : "text-gray hover:bg-surface-alt hover:text-dark"
          }`}
        >
          Active
        </button>
        <button
          type="button"
          onClick={() => setTab("history")}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            tab === "history"
              ? "bg-green-light text-green ring-1 ring-green/20"
              : "text-gray hover:bg-surface-alt hover:text-dark"
          }`}
        >
          History
        </button>
      </div>

      {tab === "history" ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <label className="text-xs text-gray">
              Terminal type
              <select
                className="input-field ml-2 !w-auto min-w-[12rem]"
                value={historyTerminal}
                onChange={(e) => setHistoryTerminal(e.target.value as typeof historyTerminal)}
              >
                <option value="all">All terminal</option>
                <option value="completed">Completed</option>
              </select>
            </label>
            <p className="text-xs text-gray-light">Up to 75 recent runs.</p>
          </div>
          {historyError ? (
            <p className="text-sm text-red">Could not load history. Try refreshing.</p>
          ) : null}
          {historyRuns && historyRuns.length === 0 && !historyError ? (
            <p className="text-sm text-gray">No matching terminal runs yet.</p>
          ) : null}
          {historyRuns && historyRuns.length > 0 ? (
            <div className="td-table-wrap">
              <table className="td-data-table min-w-[58rem]">
                <thead>
                  <tr>
                    <th>Run</th>
                    <th>Dispensary</th>
                    <th>Driver</th>
                    <th>Status</th>
                    <th>Manifest #</th>
                    <th>Manifest</th>
                    <th>Completed</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>{historyRuns.map((r) => <HistoryRow key={r.id} r={r} />)}</tbody>
              </table>
            </div>
          ) : null}
          {!historyRuns && !historyError ? <p className="text-sm text-gray">Loading history…</p> : null}
        </div>
      ) : (
        <>
          {error ? <p className="text-sm text-red">Could not load runs. Try refreshing.</p> : null}

          {runs && runs.length > 0 && staleDriverAssigned.count > 0 ? (
            <div className="rounded-2xl border border-amber bg-amber-bg px-4 py-3 text-sm text-amber-900 shadow-td-card">
              <span className="font-medium">Attention:</span> {staleDriverAssigned.count} run
              {staleDriverAssigned.count === 1 ? "" : "s"} are still assigned and waiting on the driver, with a pending
              manifest, and have not been updated since before {staleDriverAssigned.threshold}. Confirm driver acceptance
              or adjust dispatch timing.
            </div>
          ) : null}

          <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)]">
            <aside className="space-y-4 lg:border-r lg:border-td-card-border lg:pr-6">
              <div>
                <h2 className="text-xs font-medium uppercase tracking-wide text-gray">By status</h2>
                <ul className="mt-2 space-y-1">
                  <li>
                    <button
                      type="button"
                      onClick={() => setStatusFilter("all")}
                      className={`w-full rounded px-2 py-1.5 text-left text-sm transition-colors ${
                        statusFilter === "all"
                          ? "bg-green-light text-green ring-1 ring-green/20"
                          : "text-gray hover:bg-surface-alt"
                      }`}
                    >
                      All runs
                      <span className="ml-2 text-xs text-gray">({runs?.length ?? 0})</span>
                    </button>
                  </li>
                  {statusOptions.map((st) => (
                    <li key={st}>
                      <button
                        type="button"
                        onClick={() => setStatusFilter(st)}
                        className={`w-full rounded px-2 py-1.5 text-left text-sm transition-colors ${
                          statusFilter === st
                            ? "bg-green-light text-green ring-1 ring-green/20"
                            : "text-gray hover:bg-surface-alt"
                        }`}
                      >
                        <span className="text-sm text-dark">{runStatusDisplayLabel(st)}</span>
                        <span className="ml-2 text-xs text-gray">({statusCounts.get(st) ?? 0})</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <label className="block text-xs text-gray">
                Search
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by dispensary, driver, or run reference"
                  className="input-field mt-1"
                />
              </label>
            </aside>

            <div className="min-w-0 space-y-3">
              <p className="text-xs text-gray-light">
                Showing {filteredRuns.length} of {runs?.length ?? 0} runs
              </p>
              {runs && runs.length === 0 && !error ? (
                <p className="text-sm text-gray">No active runs right now.</p>
              ) : null}
              {filteredRuns.length === 0 && runs && runs.length > 0 ? (
                <p className="text-sm text-gray">No runs match the current filters.</p>
              ) : null}
              {filteredRuns.length > 0 ? (
                <div className="td-table-wrap">
                  <table className="td-data-table min-w-[52rem]">
                    <thead>
                      <tr>
                        <th>Run</th>
                        <th>Dispensary</th>
                        <th>Driver</th>
                        <th>Status</th>
                        <th>Manifest #</th>
                        <th>Manifest</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>{filteredRuns.map((r) => <RunRow key={r.id} r={r} />)}</tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </div>

          {!runs && !error ? <p className="text-sm text-gray">Loading…</p> : null}
        </>
      )}
    </section>
  );
}
